import React, { useState } from "react";
import { Info, Code, FileJson, X } from "lucide-react";
import { DataRecord, GertrudisAuditPayload } from "../types";

interface UrlContextBarProps {
  url: string;
  kpis: DataRecord;
  radiography: DataRecord;
  latestAudit?: GertrudisAuditPayload | null;
}

export function UrlContextBar({ url, kpis, radiography, latestAudit }: UrlContextBarProps) {
  const [showDataset, setShowDataset] = useState(false);

  const formatPercent = (v: any) => {
    const num = Number(v);
    if (isNaN(num)) return "—";
    return `${(num * (Math.abs(num) <= 1 ? 100 : 1)).toFixed(1)}%`;
  };

  const hasDataset = Boolean(latestAudit?.dataset);

  return (
    <div className="url-context-bar" style={{ 
        background: "var(--surface-2, #1c1c26)", 
        border: "1px solid var(--border, #2e2e3c)", 
        borderRadius: "12px", 
        padding: "16px", 
        marginBottom: "24px" 
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "16px" }}>
        
        {/* Lado izquierdo: URL y badges técnicos */}
        <div style={{ flex: 1, minWidth: "250px" }}>
          <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-dim, #8b8a99)", marginBottom: "4px" }}>
            Contexto Analizado
          </div>
          <code style={{ fontSize: "14px", display: "block", marginBottom: "8px", wordBreak: "break-all" }}>{url}</code>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", fontSize: "12px" }}>
            <span style={{ padding: "2px 8px", background: "rgba(255,255,255,0.05)", borderRadius: "12px" }}>
              {String(radiography.funnel_type || "Funnel s/d")}
            </span>
            {radiography.intencion_rota_flag ? (
              <span style={{ padding: "2px 8px", background: "rgba(239, 68, 68, 0.15)", color: "#ef4444", borderRadius: "12px" }}>Intención rota</span>
            ) : (
              <span style={{ padding: "2px 8px", background: "rgba(74, 222, 128, 0.15)", color: "#4ade80", borderRadius: "12px" }}>Intención alineada</span>
            )}
            <span style={{ padding: "2px 8px", background: "rgba(255,255,255,0.05)", borderRadius: "12px" }}>
              {String(radiography.schema_types || "Sin schema")}
            </span>
          </div>
        </div>

        {/* Lado derecho: KPIs rápidos y Botón de Dataset */}
        <div style={{ display: "flex", gap: "24px", alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "11px", color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Tráfico (30d)</div>
            <div style={{ fontSize: "16px", fontFamily: "var(--mono)", fontWeight: 600 }}>{Number(kpis.sesiones_totales_30d || 0).toLocaleString("es-CL")}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "11px", color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Posición Prom.</div>
            <div style={{ fontSize: "16px", fontFamily: "var(--mono)", fontWeight: 600 }}>{Number(kpis.posicion_promedio || 0).toFixed(1)}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "11px", color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.06em" }}>CTR</div>
            <div style={{ fontSize: "16px", fontFamily: "var(--mono)", fontWeight: 600 }}>{formatPercent(kpis.ctr_total_avg)}</div>
          </div>

          <div style={{ borderLeft: "1px solid var(--border)", paddingLeft: "24px", display: "flex", alignItems: "center" }}>
            <button 
              type="button" 
              className="button button-ghost" 
              disabled={!hasDataset}
              onClick={() => setShowDataset(true)}
              style={{ fontSize: "13px", display: "flex", alignItems: "center", gap: "6px" }}
              title={hasDataset ? "Ver los datos crudos que vio la IA" : "Dataset no disponible"}
            >
              <FileJson size={14} />
              Ver Dataset
            </button>
          </div>
        </div>
      </div>

      {/* Modal / Dialog para el Dataset */}
      {showDataset && (
        <div style={{ 
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0, 
          background: "rgba(0,0,0,0.7)", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center", padding: "20px"
        }}>
          <div style={{ 
            background: "var(--bg)", border: "1px solid var(--border)", 
            borderRadius: "12px", width: "100%", maxWidth: "800px", maxHeight: "90vh",
            display: "flex", flexDirection: "column"
          }}>
            <div style={{ 
              padding: "16px 20px", borderBottom: "1px solid var(--border)", 
              display: "flex", justifyContent: "space-between", alignItems: "center" 
            }}>
              <h3 style={{ margin: 0, fontSize: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                <Code size={18} color="var(--accent)" />
                Dataset Crudo (Input a Gertrudis)
              </h3>
              <button 
                type="button" 
                onClick={() => setShowDataset(false)}
                style={{ background: "transparent", border: "none", color: "var(--text-dim)", cursor: "pointer" }}
              >
                <X size={20} />
              </button>
            </div>
            
            <div style={{ padding: "20px", overflowY: "auto", flex: 1 }}>
              <div style={{ marginBottom: "16px", fontSize: "13px", color: "var(--text-dim)", display: "flex", gap: "6px" }}>
                <Info size={16} />
                <span>Este es el texto exacto que el modelo de IA recibió y analizó, incluyendo métricas y estructura HTML extraída.</span>
              </div>
              <pre style={{ 
                background: "var(--surface)", padding: "16px", borderRadius: "8px", 
                border: "1px solid var(--border)", overflowX: "auto", fontSize: "12px",
                whiteSpace: "pre-wrap", wordWrap: "break-word", fontFamily: "var(--mono)"
              }}>
                {latestAudit?.dataset || "No hay dataset disponible para esta auditoría."}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
